/**
 * The collapsed fifth section, labelled "Advanced settings" in every dialog: a
 * ghost button with a chevron over a Radix Collapsible, shut by default.
 *
 * `hasError` opens it: a field that fails validation inside a closed section
 * would otherwise leave the user with a toast and nothing on screen to fix.
 * It latches open on the rising edge only (state adjusted during render, the
 * React-sanctioned "store info from previous renders" pattern — no effect, no
 * ref read), so the user can still close it again while the error stands.
 */
import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface AdvancedSectionProps {
  testId: string;
  defaultOpen?: boolean;
  /** True while any field inside fails validation. */
  hasError?: boolean;
  children: ReactNode;
}

export function AdvancedSection({ testId, defaultOpen, hasError, children }: AdvancedSectionProps) {
  const { t } = useTranslation('project');
  const err = !!hasError;
  const [open, setOpen] = useState(!!defaultOpen || err);
  const [prevErr, setPrevErr] = useState(err);
  if (err !== prevErr) {
    setPrevErr(err);
    if (err) setOpen(true);
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-testid={testId}
      className='min-w-0 border-t border-slate-200/70 pt-4 dark:border-stone-700/40'>
      <CollapsibleTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          data-testid={`${testId}-trigger`}
          className='-ml-2 text-slate-600 dark:text-stone-300'>
          <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
          {t('analysisConfig.advanced')}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent data-testid={`${testId}-content`} className='mt-2 flex min-w-0 flex-col gap-4'>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
